import { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "antd";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCheck,
  Eye,
  Loader2,
  Scissors,
} from "lucide-react";

/**
 * Barra de ações da questão (carrossel) — o "Card Footer" do design.
 *
 * Desktop: rodapé do card da questão (borda superior, fundo muted), com
 * Anterior à esquerda e dica de atalho + Confirmar + Próxima à direita.
 * Respondida: Confirmar vira o estado travado "Respondida" e a primária
 * passa a ser Próxima — o próximo passo natural.
 *
 * Compacto (mobile): barra ancorada no rodapé via portal — Anterior só ícone,
 * Confirmar ocupa a largura, Próxima mantém rótulo. O Gabarito fica como
 * ícone porque no mobile não há painel lateral de ferramentas.
 *
 * O modo compacto renderiza via portal no <body>: dentro de Tabs/Spin do antd
 * há ancestrais com transform/filter que viram containing block e prendem o
 * position:fixed ao conteúdo — no Android a barra "afundava" para o fim do
 * scroll. Não remova o portal sem testar lá.
 *
 * Componente puramente visual — todos os handlers vêm de fora (§45).
 */
export interface QuestionActionBarProps {
  /** Abaixo de lg: barra ancorada no rodapé via portal. */
  compact?: boolean;

  onPrevious: () => void;
  onNext: () => void;
  /** Paginação/carregamento em andamento. */
  navigating?: boolean;

  /** Usado apenas no modo compacto — no desktop o Gabarito vive no painel lateral. */
  onRevealAnswer: () => void;
  onConfirm: () => void;
  /** Requisição de resposta em andamento. */
  answering?: boolean;
  /** Questão já respondida ou revisada — trava Gabarito e Confirmar. */
  answered?: boolean;

  /** Dica curta exibida antes das ações no desktop (ex.: atalhos de teclado). */
  hint?: string;

  /** Itens + alternativas riscados pela tesoura; > 0 mostra o contador no rodapé. */
  scissorCount?: number;
  /** Rótulo do contador (ex.: "1 eliminada", "2 riscados"). */
  scissorLabel?: string;
  /** Desfaz todas as marcações da tesoura (itens e alternativas). */
  onRestoreAll?: () => void;
  className?: string;
}

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(" ");

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-surface";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-input " +
  "transition-colors duration-150 motion-reduce:transition-none " +
  "disabled:cursor-not-allowed";

const SECONDARY =
  "border border-edge bg-surface font-medium text-ink-secondary " +
  "hover:border-edge-strong hover:bg-surface-hover hover:text-ink " +
  "disabled:opacity-50 disabled:hover:border-edge disabled:hover:bg-surface";

const PRIMARY =
  "bg-brand font-semibold text-brand-contrast " +
  "transition-[filter,opacity] hover:brightness-110 disabled:opacity-50";

/** Estado travado "Respondida" — visível, mas claramente inerte (§2.4). */
const DONE = "bg-surface-muted font-semibold text-ink-muted";

export default function QuestionActionBar({
  compact = false,
  onPrevious,
  onNext,
  navigating = false,
  onRevealAnswer,
  onConfirm,
  answering = false,
  answered = false,
  hint,
  scissorCount = 0,
  scissorLabel,
  onRestoreAll,
  className,
}: QuestionActionBarProps) {
  const height = compact ? "h-12" : "h-10";
  const textSize = compact ? "text-[15px]" : "text-sm";

  const spinnerOr = (node: ReactNode) =>
    navigating ? <Loader2 className="h-[18px] w-[18px] animate-spin" aria-hidden="true" /> : node;

  const previousButton = (
    <button
      type="button"
      onClick={onPrevious}
      disabled={navigating}
      aria-label="Anterior"
      className={cx(BASE, SECONDARY, FOCUS, height, textSize, "shrink-0", compact ? "w-12 px-0" : "px-4")}
    >
      {spinnerOr(
        <>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {!compact && <span>Anterior</span>}
        </>,
      )}
    </button>
  );

  const nextButton = (
    <button
      type="button"
      onClick={onNext}
      disabled={navigating}
      aria-label="Próxima"
      className={cx(
        BASE,
        answered ? PRIMARY : SECONDARY,
        FOCUS,
        height,
        textSize,
        "shrink-0",
        compact ? "px-4" : "px-4",
      )}
    >
      {spinnerOr(
        <>
          <span>Próxima</span>
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </>,
      )}
    </button>
  );

  const confirmButton = answered && !answering ? (
    <button
      type="button"
      disabled
      className={cx(BASE, DONE, height, textSize, compact ? "min-w-0 flex-1 px-3" : "px-5")}
    >
      <CheckCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="truncate">Respondida</span>
    </button>
  ) : (
    <button
      type="button"
      onClick={onConfirm}
      disabled={answering}
      className={cx(BASE, PRIMARY, FOCUS, height, textSize, compact ? "min-w-0 flex-1 px-3" : "px-5")}
    >
      {answering ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <Check className="h-4 w-4 shrink-0" strokeWidth={3} aria-hidden="true" />
      )}
      <span className="truncate">
        {answering ? "Enviando…" : compact ? "Confirmar resposta" : "Confirmar resposta"}
      </span>
    </button>
  );

  if (compact) {
    const bar = (
      <div
        role="toolbar"
        aria-label="Ações da questão"
        className={cx(
          // z-[900], abaixo dos 1000 dos overlays do antd: Drawer (filtro),
          // Modal e afins devem cobrir a barra.
          "fixed inset-x-0 bottom-0 z-[900] flex items-center gap-2.5",
          "border-t border-edge bg-surface/95 px-4 pt-2.5 backdrop-blur",
          "pb-[max(0.625rem,env(safe-area-inset-bottom))] shadow-panel",
          className,
        )}
      >
        {previousButton}
        {!answered && (
          <Tooltip title="Gabarito" placement="top">
            <button
              type="button"
              onClick={onRevealAnswer}
              disabled={answering}
              aria-label="Gabarito"
              className={cx(BASE, SECONDARY, FOCUS, height, "w-12 shrink-0 px-0")}
            >
              <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
            </button>
          </Tooltip>
        )}
        {confirmButton}
        {nextButton}
      </div>
    );

    // Ver o comentário do cabeçalho: fora do portal, ancestrais com transform
    // prendem o fixed e a barra afunda para o fim do scroll no Android.
    return createPortal(bar, document.body);
  }

  return (
    <div
      role="toolbar"
      aria-label="Ações da questão"
      className={cx(
        "flex items-center gap-3 border-t border-edge bg-surface-muted px-5 py-4",
        className,
      )}
    >
      {previousButton}

      {scissorCount > 0 && !answered ? (
        <span className="flex min-w-0 items-center gap-1.5 text-xs">
          <Scissors size={14} className="shrink-0 text-ink-secondary" aria-hidden />
          <span className="truncate font-semibold text-ink-secondary">
            {scissorLabel ?? `${scissorCount} riscado${scissorCount === 1 ? "" : "s"}`}
          </span>
          {onRestoreAll ? (
            <>
              <span aria-hidden className="text-ink-muted">
                ·
              </span>
              <button
                type="button"
                onClick={onRestoreAll}
                className="shrink-0 font-semibold text-brand hover:underline"
              >
                Restaurar todas
              </button>
            </>
          ) : null}
        </span>
      ) : null}

      <div className="ml-auto flex min-w-0 items-center gap-3">
        {hint ? (
          <span className="hidden truncate text-xs text-ink-muted xl:block">{hint}</span>
        ) : null}
        {confirmButton}
        {nextButton}
      </div>
    </div>
  );
}
